import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import type { ApiResource, ApiAction, ApiPermission } from "@/types";
import { permissionService, type ModuleId, type ModuleAction } from "@/services/permissionService";
import { permissionDataStore } from "@/services/permissionDataStore";
import apiService, { setUnauthorizedCallback, DeviceFilter, type EntitlementConfig, type CameraEntitlementConfig, type AccountsResponse, type ConsumerUserWithLocations, type ConsumerAccountWithLocations, type ConsumerLocationDetail } from "@/services/apiService";
import { useIdleTimer } from "@/hooks/useIdleTimer";
import { IdleWarningModal } from "@/components/IdleWarningModal/IdleWarningModal";
import { useDynamicModal } from "@resideo/blueprint-oneportal";
import { queryClient } from "@/queryClient";

// Application version for cache busting
const APP_VERSION = "2.0.0";

/**
 * User context from API
 * roleName and permissions are optional — they are populated after context selection
 */
interface UserContext {
  context: string;
  contextDisplayName: string;
  roleName?: string;
  permissions?: Array<{
    resource: ApiResource;
    actions: ApiAction[];
  }>;
}

/**
 * User data structure
 */
interface UserData {
  id: string;
  name: string;
  email: string;
  contexts: UserContext[];
}

/**
 * Location data from consumer accounts
*/
export interface LocationData {
  id: string;
  name: string;
  consumerAccountId?: string;
}

/**
 * Auth context value
 */
interface AuthContextValue {
  user: UserData | null;
  selectedContext: string | null;
  activeRole: string | null;
  locations: LocationData[];
  selectedLocation: LocationData | null;
  selectLocation: (location: LocationData | null) => void;
  consumerUsers: any[];
  selectedCompanyIndex: number;
  selectCompany: (index: number) => void;
  isInitializing: boolean;
  isSelectingContext: boolean;
  isLoadingPermissions: boolean;
  isRefreshingPermissions: boolean;
  login: (userData: UserData) => Promise<void>;
  selectContext: (context: string) => void;
  logout: () => void;
  showInvalidCredentialsModal: () => void;
  hasPermission: (permission: string) => boolean;
  hasModulePermission: (moduleId: ModuleId, action: ModuleAction) => boolean;
  hasResourcePermission: (resource: ApiResource, action: ApiAction) => boolean;
  canAccessModule: (moduleId: ModuleId) => boolean;
  getAccessibleModules: () => ModuleId[];
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Convert old API permission format to dot notation
 */
export function convertPermissionsToDotNotation(
  oldPermissions: Array<string | { resource?: string; actions?: string[] }>
): string[] {
  const actionMap: Record<string, string> = {
    READ: "view",
    CREATE: "create",
    UPDATE: "edit",
    DELETE: "delete",
    EXECUTE: "operate",
    MANAGE: "manage",
    CONFIGURE: "configure",
    INVITE: "invite",
    HIDE: "hide",
    OPERATE: "operate",
    REMOVE: "delete",
    ASSIGN_ROLE: "manage",
    UPDATE_ROLE: "manage",
    SUBSCRIBE: "manage",
    UNSUBSCRIBE: "manage",
    GRANT_CONSENT: "manage",
    STATUS: "status",
    EXIT: "exit",
    MAP: "map",
    NOTIFICATION_DELETE: "notification_delete",
    NOTIFICATION_UPDATE: "notification_update",
    NOTIFICATION_VIEW: "notification_view",
    PARTNER_LINK: "partner_link",
    PARTNER_UNLINK: "partner_unlink",
    STRUCTURE_CREATE: "structure_create",
    STRUCTURE_DELETE: "structure_delete",
    STRUCTURE_UPDATE: "structure_update",
  };

  const resourceMap: Record<string, string> = {
    ACCOUNT: "account",
    ACTIVITY_FEED: "activity_feed",
    BRANDING: "branding",
    DEVICE: "device",
    GEOFENCE: "geofence",
    LOCATION: "location",
    PROFILE: "profile",
    REPORTS: "reports",
    SERVICES: "services",
    SUBSCRIPTION: "subscription",
    USER: "user",
  };

  // Per-resource overrides applied before the generic actionMap fallback.
  const resourceActionOverrides: Record<string, Record<string, string>> = {
    clip: { READ: "read" },
  };

  const dotNotationPermissions: string[] = [];

  for (const perm of oldPermissions) {
    if (typeof perm === "string") {
      if (perm) dotNotationPermissions.push(perm);
      continue;
    }

    if (!perm || !perm.resource || !perm.actions) continue;

    const resource = resourceMap[perm.resource] || perm.resource.toLowerCase();

    for (const action of perm.actions) {
      if (!action) continue;
      const mappedAction =
        resourceActionOverrides[resource]?.[action] ??
        actionMap[action] ??
        action.toLowerCase();
      dotNotationPermissions.push(`${resource}.${mappedAction}`);
    }
  }

  return dotNotationPermissions;
}

/**
 * Load permissions for a context
 */
async function loadPermissionsForContext(
  context: string,
  role: string,
  rolePermissions: Array<{ roleName: string; permissions: Array<{ resource: string; actions: string[] }> }>
): Promise<void> {
  const roleData = rolePermissions.find((r) => r.roleName === role);
  if (!roleData) {
    console.warn(`⚠️ No permissions found for role: ${role}`);
    return;
  }

  const dotNotationPermissions = convertPermissionsToDotNotation(roleData.permissions);

  // Load permissions into permissionService (just the permissions array)
  permissionService.loadPermissions(dotNotationPermissions);

  await permissionDataStore.set(context, role, roleData.permissions as ApiPermission[]);

  console.log("✅ Permissions loaded:", {
    context,
    role,
    permissionCount: dotNotationPermissions.length,
  });
}

/**
 * Keys (or key prefixes) whose localStorage values must survive auth-related
 * clears so that user preferences (camera order, view size) are not lost on
 * logout or version-change cache busts.
 */
const PERSISTENT_STORAGE_KEY_PREFIXES = [
  'camera-order',
  'camera-view-size',
  'custom-view-size',
  'timeline-swimlane-settings',
  'camera-bounding-boxes-visible',
  'camera-bounding-boxes' // Preserve bounding box preferences across logout
];

/** localStorage.clear() replacement that preserves camera preference keys. */
function clearStoragePreservingPrefs(): void {
  const preserved: Array<[string, string]> = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && PERSISTENT_STORAGE_KEY_PREFIXES.some((p) => key === p || key.startsWith(`${p}-`))) {
      const value = localStorage.getItem(key);
      if (value !== null) preserved.push([key, value]);
    }
  }
  localStorage.clear();
  preserved.forEach(([key, value]) => localStorage.setItem(key, value));
}

// ─── Entitlement caching ─────────────────────────────────────────────────────
// The per-device camera entitlement configs are cached in localStorage so that
// pilets consuming `api.getPerDeviceEntitlementConfig()` see the latest known
// per-device values immediately (before the next API round-trip completes).
// NOTE: `/api/v1/devices/entitlementConfigs` is ALWAYS re-fetched after every
// `/api/v2/accounts?accountType={context}` call — the cache is never used to
// skip that network round-trip, even when the scope's deviceIds are unchanged.
const ENTITLEMENT_DEVICE_CACHE_KEY = "oneportal_entitlement_configs_by_device";

function readEntitlementDeviceCache(): Record<string, CameraEntitlementConfig | null> {
  try {
    const raw = localStorage.getItem(ENTITLEMENT_DEVICE_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeEntitlementDeviceCache(map: Record<string, CameraEntitlementConfig | null>): void {
  try {
    localStorage.setItem(ENTITLEMENT_DEVICE_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore storage errors */
  }
}

/**
 * Collect physical deviceIds from cached consumer-accounts data.
 * When `locationId` is provided, only devices at that location are returned.
 * When omitted (org-level view / "My Organization"), ALL devices across
 * every location of every consumer user are returned.
 */
function extractDeviceIdsFromConsumerData(
  consumerData: any,
  locationId?: string,
): string[] {
  const users: any[] = consumerData?.data?.consumerUsers || [];
  const ids: string[] = [];
  for (const user of users) {
    const locations: any[] = user?.consumerAccount?.locations || [];
    for (const loc of locations) {
      if (locationId && loc?.id !== locationId) continue;
      const devices: any[] = loc?.consumerDevices || [];
      for (const dev of devices) {
        // Prefer the physical device id used by every other camera API
        const id: string | undefined = dev?.device?.deviceId ?? dev?.id;
        if (id) ids.push(id);
      }
    }
  }
  // Deduplicate in case a device appears more than once
  return Array.from(new Set(ids));
}

/**
 * Auth Provider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isSelectingContext, setIsSelectingContext] = useState<boolean>(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState<boolean>(true);
  const [isRefreshingPermissions, setIsRefreshingPermissions] = useState<boolean>(false);
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    null,
  );
  // Stable ref so event handlers always read the latest selectedLocation without stale closure
  const selectedLocationRef = useRef<LocationData | null>(null);
  useEffect(() => { selectedLocationRef.current = selectedLocation; }, [selectedLocation]);
  const [consumerUsers, setConsumerUsers] = useState<any[]>([]);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState<number>(0);
  const [showIdleWarning, setShowIdleWarning] = useState<boolean>(false);
  const [idleWarningSeconds, setIdleWarningSeconds] = useState<number>(30);

  const { openDynamicModal, DynamicModalHost } = useDynamicModal();

  /**
   * Show "Invalid Username or Password" modal and trigger logout on Ok.
   * Used for blocked PARTNER context restoration and for any
   * failure of the /v1/accounts/accountTypes call (missing/empty response
   * or no consumerAccountTypes).
   */
  const showInvalidCredentialsModal = (): void => {
    openDynamicModal(
      {
        ActionButtons: [{ label: 'Ok', variant: 'primary' as const }],
        ActionCallBacks: ['ok_action'],
        Data: { BodyText1: 'Invalid Username or Password. Please try again.' },
      },
      { ok_action: { callbackFunction: () => logout(), closeModal: true } },
    );
  };

  /**
   * Show "missing partner credential" modal without logging the user out.
   * Used when a user with multiple contexts (consumer + partner) picks the
   * PARTNER option on the context-selection page — they should stay on that
   * page and be able to pick a consumer context instead.
   */
  const showPartnerCredentialMissingModal = (): void => {
    openDynamicModal(
      {
        ActionButtons: [{ label: 'Ok', variant: 'primary' as const }],
        ActionCallBacks: ['ok_action'],
        Data: { BodyText1: "You don't have a credential set for this account" },
      },
      { ok_action: { callbackFunction: () => { }, closeModal: true } },
    );
  };

  const navigate = useNavigate();
  const { isLoading: auth0Loading, logout: auth0Logout } = useAuth0();

  const hasRestoredSessionRef = useRef(false);
  const skipRoleCheckRef = useRef(false);

  /**
   * Restore session from localStorage
   */
  useEffect(() => {
    const restoreSession = async () => {
      // Prevent multiple executions
      if (hasRestoredSessionRef.current) return;
      hasRestoredSessionRef.current = true;

      // Don't restore on callback page
      if (window.location.pathname === "/callback") {
        setIsInitializing(false);
        setIsLoadingPermissions(false);
        return;
      }

      // Don't restore session on public pages — they don't need auth and
      // shouldn't trigger consumer-accounts API calls on load.
      const publicPathPrefixes = ["/vav"];
      const currentPath = window.location.pathname;
      if (publicPathPrefixes.some((p) => currentPath === p || currentPath.startsWith(p + "/"))) {
        setIsInitializing(false);
        setIsLoadingPermissions(false);
        return;
      }

      // Check for version change (cache busting)
      const storedVersion = localStorage.getItem("oneportal_app_version");
      if (storedVersion && storedVersion !== APP_VERSION) {
        console.log("🔄 App version changed, clearing cache");
        clearStoragePreservingPrefs();
        sessionStorage.clear();
        permissionDataStore.clear();
        localStorage.setItem("oneportal_app_version", APP_VERSION);
        setIsInitializing(false);
        setIsLoadingPermissions(false);
        return;
      }

      // Store current version
      if (!storedVersion) {
        localStorage.setItem("oneportal_app_version", APP_VERSION);
      }

      // Try to restore session
      const storedUser = localStorage.getItem("oneportal_user");
      const storedContext = localStorage.getItem("oneportal_selected_context");

      if (!storedUser || !storedContext) {
        console.log("❌ No cached session found");
        setIsInitializing(false);
        setIsLoadingPermissions(false);
        return;
      }

      // Block PARTNER context restore — temporary restriction
      if (storedContext === "PARTNER") {
        localStorage.removeItem("oneportal_user");
        localStorage.removeItem("oneportal_selected_context");
        setIsInitializing(false);
        setIsLoadingPermissions(false);
        showInvalidCredentialsModal();
        return;
      }

      try {
        const userData: UserData = JSON.parse(storedUser);
        const contextData = userData.contexts.find((c) => c.context === storedContext);

        if (!contextData) {
          console.warn("⚠️ Stored context not found in user data");
          localStorage.removeItem("oneportal_selected_context");
          setIsInitializing(false);
          setIsLoadingPermissions(false);
          return;
        }

        let restoredSelectedLocation: LocationData | null = null;
        const storedSelectedLocation = localStorage.getItem(
          "oneportal_selected_location",
        );
        if (storedSelectedLocation) {
          try {
            restoredSelectedLocation = JSON.parse(storedSelectedLocation);
          } catch {
            /* ignore parse errors */
          }
        }

        // Restore user state
        setUser(userData);
        setSelectedContext(storedContext);
        setActiveRole(contextData.roleName || null);

        // Load cached permissions initially (will be refreshed from API below)
        if (contextData.roleName && contextData.permissions) {
          await loadPermissionsForContext(storedContext, contextData.roleName, [
            {
              roleName: contextData.roleName,
              permissions: contextData.permissions,
            },
          ]);
        }
        // Restore locations from API for consumer contexts
        if (storedContext === "RESIDENTIAL" || storedContext === "COMMERCIAL") {
          try {
            const consumerData = await apiService.getConsumerAccounts(storedContext, DeviceFilter.CAMERAS);
            // Refresh the cached consumer data so pilets see fresh data (incl. newly-supported device families)
            try {
              localStorage.setItem(
                "oneportal_consumer_data_full",
                JSON.stringify(consumerData),
              );
            } catch {
              /* ignore storage errors */
            }
            const fetchedConsumerUsers = consumerData?.data?.consumerUsers || [];

            // Always refresh role and permissions from API (localStorage may have stale permissions).
            // If a location is already selected, use the role for the account that owns that location.
            const selectedLocationUser = restoredSelectedLocation
              ? fetchedConsumerUsers.find((cu: any) => {
                const accountLocations = cu?.consumerAccount?.locations || [];
                return accountLocations.some(
                  (loc: any) => loc?.id === restoredSelectedLocation?.id,
                );
              })
              : undefined;
            const freshRole = selectedLocationUser?.role || fetchedConsumerUsers[0]?.role || contextData.roleName;
            if (freshRole) {
              setActiveRole(freshRole);
              const permissionsResponse = await apiService.getRolePermissions("CUSTOMER");
              const roleData = permissionsResponse.data?.roles?.find(
                (role: any) => role.roleName === freshRole,
              );
              const freshPermissions = roleData?.permissions || [];
              contextData.roleName = freshRole;
              contextData.permissions = freshPermissions as any;
              setUser({ ...userData });
              localStorage.setItem("oneportal_user", JSON.stringify(userData));
              await loadPermissionsForContext(storedContext, freshRole, [
                { roleName: freshRole, permissions: freshPermissions },
              ]);
            }

            if (storedContext === "COMMERCIAL" && fetchedConsumerUsers.length > 1) {
              setConsumerUsers(fetchedConsumerUsers);
              setSelectedCompanyIndex(0);
              writeSelectedCompanyIndex(0);
              const companyLocations = extractLocationsFromConsumerUser(fetchedConsumerUsers[0]);
              setLocations(companyLocations);
              // Scope pilet-facing consumer data to the first company so "My
              // Organization" shows only that company's devices (matching the
              // default company-select value).
              writePiletConsumerData(buildScopedConsumerData(consumerData, fetchedConsumerUsers[0]));
            } else {
              setConsumerUsers([]);
              setSelectedCompanyIndex(0);
              writeSelectedCompanyIndex(null);
              const accountLocations: LocationData[] = fetchedConsumerUsers.flatMap(
                (cu: any) => extractLocationsFromConsumerUser(cu),
              );
              setLocations(accountLocations);
              // Single-company / RESIDENTIAL: pilet sees the full payload.
              writePiletConsumerData(consumerData);
            }
          } catch {
            /* ignore fetch errors — locations will be empty */
          }
        }
        if (restoredSelectedLocation) {
          setSelectedLocation(restoredSelectedLocation);
        }

        console.log("✅ Session restored successfully");
      } catch (error) {
        console.error("❌ Failed to restore session:", error);
        localStorage.removeItem("oneportal_user");
        localStorage.removeItem("oneportal_selected_context");
      }

      setIsInitializing(false);
      setIsLoadingPermissions(false);
    };

    if (!auth0Loading) {
      restoreSession();
    }
  }, [auth0Loading]);

  // Sync locations state when pilets call refreshConsumerData() (e.g. after device delete/add).
  // refreshConsumerData only writes to localStorage; this effect is the bridge back into React state.
  useEffect(() => {
    const handleRefresh = (event: Event) => {
      if (!selectedContext || !isConsumerContext(selectedContext)) return;
      const freshData = (event as CustomEvent<AccountsResponse>).detail;
      const fetchedConsumerUsers: ConsumerUserWithLocations[] = freshData?.data?.consumerUsers || [];
      const toLocations = (cu: ConsumerUserWithLocations): LocationData[] => {
        const account: ConsumerAccountWithLocations | undefined = cu?.consumerAccount;
        return (account?.locations || [])
          .filter((loc: ConsumerLocationDetail) => loc.id && loc.name)
          .map((loc: ConsumerLocationDetail) => ({
            id: loc.id,
            name: loc.name,
            consumerAccountId: account?.id || undefined,
          }));
      };
      let newLocations: LocationData[];
      if (selectedContext === 'COMMERCIAL' && fetchedConsumerUsers.length > 1) {
        const idx = selectedCompanyIndex >= 0 && selectedCompanyIndex < fetchedConsumerUsers.length
          ? selectedCompanyIndex : 0;
        newLocations = toLocations(fetchedConsumerUsers[idx]);
      } else {
        newLocations = fetchedConsumerUsers.flatMap(toLocations);
      }
      setLocations(newLocations);
      // If the currently selected location was removed, clear it so pilets stop rendering stale data
      const current = selectedLocationRef.current;
      if (current && !newLocations.some(l => l.id === current.id)) {
        setSelectedLocation(null);
        localStorage.removeItem('oneportal_selected_location');
        window.dispatchEvent(new CustomEvent('oneportal:location-changed', { detail: null }));
      }
    };
    window.addEventListener('oneportal:consumer-data-refreshed', handleRefresh);
    return () => window.removeEventListener('oneportal:consumer-data-refreshed', handleRefresh);
  }, [selectedContext, selectedCompanyIndex]);

  /**
   * Login - store user data and redirect to context selection
   */
  const login = async (userData: UserData): Promise<void> => {
    setUser(userData);
    localStorage.setItem("oneportal_user", JSON.stringify(userData));

    // If user has only one context, auto-select it
    if (userData.contexts.length === 1) {
      const context = userData.contexts[0].context;
      await selectContextWithUser(context, userData);
    } else {
      // Navigate to context selection
      navigate("/context-selection", { replace: true });
    }
  };

  /**
   * * Determine if a context is a consumer-type context (RESIDENTIAL or COMMERCIAL)
  */
  const isConsumerContext = (context: string): boolean => {
    return context === "RESIDENTIAL" || context === "COMMERCIAL";
  };

  /**
   * Extract locations from a specific consumerUser's account
   */
  const extractLocationsFromConsumerUser = (cu: any): LocationData[] => {
    const account = cu?.consumerAccount;
    return (account?.locations || [])
      .filter((loc: any) => loc?.id && loc?.name)
      .map((loc: any) => ({
        id: loc.id,
        name: loc.name,
        consumerAccountId: account?.id || undefined,
      }));
  };

  /**
   * Return a consumer-data payload with `data.consumerUsers` scoped to a
   * single company (used by the pilet-facing `oneportal_consumer_data`
   * localStorage entry). For multi-company COMMERCIAL accounts this ensures
   * the pilet's org-level ("My Organization") view shows devices only for
   * the currently-selected company from the `company-select` dropdown, not
   * for every company on the account.
   *
   * `consumerUser` is expected to be one entry from `data.consumerUsers`.
   * If `fullData` is falsy the function returns a minimal wrapper object.
   */
  const buildScopedConsumerData = (fullData: any, consumerUser: any): any => {
    const base = fullData ?? { data: {} };
    return {
      ...base,
      data: {
        ...(base.data ?? {}),
        consumerUsers: consumerUser ? [consumerUser] : [],
      },
    };
  };

  /**
   * Persist the pilet-facing consumer-data payload to localStorage.
   * The pilet reads this via `api.getConsumerData()` when building the
   * org-level ("My Organization") device list.
   */
  const writePiletConsumerData = (payload: any): void => {
    try {
      localStorage.setItem(
        "oneportal_consumer_data",
        JSON.stringify(payload),
      );
    } catch {
      /* ignore storage errors */
    }
  };

  /**
   * Persist the currently-selected company index for multi-company COMMERCIAL
   * accounts (or clear it when passing `null`). Wrapped in try/catch so that
   * a failed localStorage write (disabled storage, quota exceeded, Safari
   * private mode, etc.) does not abort the surrounding initialization —
   * callers assume state updates (setLocations, writePiletConsumerData, …)
   * still run even if persistence fails.
   */
  const writeSelectedCompanyIndex = (index: number | null): void => {
    try {
      if (index === null) {
        localStorage.removeItem("oneportal_selected_company_index");
      } else {
        localStorage.setItem("oneportal_selected_company_index", String(index));
      }
    } catch {
      /* ignore storage errors */
    }
  };

  /**
   * Purge the shared React Query cache so pilets do not serve device lists
   * cached under the previous context/company scope. Called AFTER the
   * pilet-facing `oneportal_consumer_data` has been rewritten so that any
   * active `useQuery` observers refetch against the new scope, not the old.
   * Also cancels in-flight queries first — without this, a request kicked
   * off before the localStorage update could still write stale data into
   * cache after removal.
   */
  const purgePiletQueryCache = async (): Promise<void> => {
    try {
      await queryClient.cancelQueries();
    } catch {
      /* cancellation errors are non-fatal */
    }
    queryClient.removeQueries();
  };

  /**
   * Switch company for COMMERCIAL accounts with multiple consumerUsers.
   * Updates locations to show only the selected company's locations, and
   * rewrites the pilet-facing `oneportal_consumer_data` so the pilet's
   * org-level ("My Organization") view is filtered to the selected company.
   *
   * The shared React Query cache is purged AFTER the localStorage update so
   * pilet observers refetch against the new company scope.
   */
  const selectCompany = (index: number): void => {
    if (index < 0 || index >= consumerUsers.length) return;
    const nextCompany = consumerUsers[index];
    setSelectedCompanyIndex(index);
    writeSelectedCompanyIndex(index);
    const companyLocations = extractLocationsFromConsumerUser(nextCompany);
    setLocations(companyLocations);

    // Scope the pilet-facing consumer data to the selected company so
    // `api.getConsumerData()` returns only this company's consumerUser.
    let fullData: any = null;
    try {
      const raw = localStorage.getItem("oneportal_consumer_data_full");
      fullData = raw ? JSON.parse(raw) : null;
    } catch {
      fullData = null;
    }
    writePiletConsumerData(buildScopedConsumerData(fullData, nextCompany));

    // Purge cached queries AFTER localStorage is updated so refetches use
    // the new company scope, not the previous one.
    purgePiletQueryCache();

    // Auto-select first location of the new company
    if (companyLocations.length > 0) {
      selectLocation(companyLocations[0]);
    } else {
      selectLocation(null);
    }
  };

  /**
    * Select context with explicit user data (for login flow)
    * Fetches account details + role + permissions for the selected context
    */
  const selectContextWithUser = async (context: string, userData: UserData): Promise<void> => {
    // Block PARTNER context — temporary restriction.
    // Behavior depends on how many contexts the user has available:
    //  - Multi-context (consumer + partner): show "missing credential" message
    //    and keep the user on the context-selection page so they can pick a
    //    consumer context instead. Do NOT logout.
    //  - Single-context (partner-only): keep the existing logout flow.
    //    In practice Auth0Callback already blocks partner-only accounts before
    //    they reach this page, so this branch is a safety net.
    if (context === "PARTNER") {
      if (userData.contexts.length > 1) {
        showPartnerCredentialMissingModal();
        return;
      }

      // Ensure we don't leave a partially-restored user in state/localStorage
      setUser(null);
      setSelectedContext(null);
      setActiveRole(null);
      localStorage.removeItem("oneportal_user");
      localStorage.removeItem("oneportal_selected_context");

      showInvalidCredentialsModal();
      return;
    }

    console.log("🎯 Context selection:", context);
    setIsSelectingContext(true);
    setIsLoadingPermissions(true);
    // Skip role check for location changes during context selection
    skipRoleCheckRef.current = true;

    try {
      const contextData = userData.contexts.find((c) => c.context === context);
      if (!contextData) {
        throw new Error(`Context ${context} not found`);
      }

      setSelectedContext(context);
      localStorage.setItem("oneportal_selected_context", context);

      let roleName: string | undefined;
      let rawPermissions: Array<{ resource: string; actions: string[] }> = [];
      if (isConsumerContext(context)) {
        const consumerData = await apiService.getConsumerAccounts(context, DeviceFilter.CAMERAS);
        roleName = consumerData?.data?.consumerUsers?.[0]?.role || undefined;

        // Keep a full copy for later company switches (used by `selectCompany`
        // to rebuild the scoped payload without a re-fetch).
        try {
          localStorage.setItem(
            "oneportal_consumer_data_full",
            JSON.stringify(consumerData),
          );
        } catch {
          /* ignore storage errors */
        }

        // Extract consumerUsers array
        const fetchedConsumerUsers = consumerData?.data?.consumerUsers || [];

        // Determine the initial company scope for the pilet-facing consumer
        // data. For multi-company COMMERCIAL accounts we default to the first
        // company so "My Organization" shows only that company's devices
        // (matching the initial value of the `company-select` dropdown).
        // For RESIDENTIAL / single-company COMMERCIAL the full payload is
        // stored so existing behavior is preserved.
        const isMultiCompanyCommercial =
          context === "COMMERCIAL" && fetchedConsumerUsers.length > 1;
        const piletConsumerData = isMultiCompanyCommercial
          ? buildScopedConsumerData(consumerData, fetchedConsumerUsers[0])
          : consumerData;
        writePiletConsumerData(piletConsumerData);

        // Purge cached queries AFTER the pilet-facing consumer data has been
        // rewritten. Cancelling in-flight queries first prevents a request
        // kicked off before the localStorage update from writing stale data
        // into the cache after removal.
        await purgePiletQueryCache();

        // Load subscription entitlements from devices API (non-blocking — must not interrupt login)
        try {
          const firstUser = consumerData?.data?.consumerUsers?.[0];
          const locationId: string | undefined = firstUser?.consumerAccount?.locations?.[0]?.id;
          // Default scope after context selection matches the location that
          // will be auto-selected below (first location of first user).
          // If no location exists yet, fall through to org-level (undefined).
          await refreshEntitlements(consumerData, locationId);
        } catch {
          /* ignore entitlement errors — they do not block login */
        }

        // For COMMERCIAL with multiple companies, show only the first company's locations
        if (isMultiCompanyCommercial) {
          setConsumerUsers(fetchedConsumerUsers);
          setSelectedCompanyIndex(0);
          writeSelectedCompanyIndex(0);
          const companyLocations = extractLocationsFromConsumerUser(fetchedConsumerUsers[0]);
          setLocations(companyLocations);
          if (companyLocations.length > 0) {
            selectLocation(companyLocations[0]);
          } else {
            // No locations in the new company — explicitly reset so the pilet
            // clears any previously-selected location from the prior context.
            selectLocation(null);
          }
        } else {
          // RESIDENTIAL or single-company COMMERCIAL: flatten all locations (existing behavior)
          setConsumerUsers([]);
          setSelectedCompanyIndex(0);
          writeSelectedCompanyIndex(null);
          const accountLocations: LocationData[] = fetchedConsumerUsers.flatMap(
            (cu: any) => extractLocationsFromConsumerUser(cu),
          );
          setLocations(accountLocations);
          if (accountLocations.length > 0) {
            selectLocation(accountLocations[0]);
          } else {
            // No locations in the new context — explicitly reset so the pilet
            // clears any previously-selected location from the prior context.
            selectLocation(null);
          }
        }
        if (roleName) {
          const permissionsResponse =
            await apiService.getRolePermissions("CUSTOMER");
          const roleData = permissionsResponse.data?.roles?.find(
            (role: any) => role.roleName === roleName,
          );
          rawPermissions = roleData?.permissions || [];
        }
      } else if (context === "PARTNER") {
        // Partner flow: fetch partner accounts, then role permissions
        const partnerData = await apiService.getPartnerAccounts();
        roleName = partnerData?.data?.[0]?.roles?.[0] || undefined;
        // Clear locations and consumer data for partner context
        setLocations([]);
        setConsumerUsers([]);
        setSelectedCompanyIndex(0);
        selectLocation(null);
        localStorage.removeItem("oneportal_consumer_data");
        localStorage.removeItem("oneportal_consumer_data_full");
        writeSelectedCompanyIndex(null);
        // Purge cached queries AFTER consumer data has been cleared so any
        // pilet observers refetch against the empty scope, not the previous
        // consumer context's data.
        await purgePiletQueryCache();
        if (roleName) {
          const permissionsResponse =
            await apiService.getRolePermissions("PARTNER");
          const roleData = permissionsResponse.data?.roles?.find(
            (role: any) => role.roleName === roleName,
          );
          rawPermissions = roleData?.permissions || [];
        }
      }
      if (!roleName) {
        throw new Error(`No role found for context: ${context}`);
      }
      setActiveRole(roleName);
      // Update context data with fetched role and permissions
      contextData.roleName = roleName;
      contextData.permissions = rawPermissions as any;
      // Persist enriched user data to localStorage
      setUser({ ...userData });
      localStorage.setItem("oneportal_user", JSON.stringify(userData));

      // Load permissions for this context
      const rolePermissions = [
        {
          roleName,
          permissions: rawPermissions,
        },
      ];
      await loadPermissionsForContext(context, roleName, rolePermissions);

      // Navigate to appropriate dashboard
      const targetPath = context === "PARTNER" ? "/mybusiness/home" : "/dashboard";
      navigate(targetPath, { replace: true });
    } catch (error) {
      console.error("❌ Failed to select context:", error);
      throw error;
    } finally {
      setIsSelectingContext(false);
      setIsLoadingPermissions(false);
    }
  };

  /**
   * Select context and load permissions
   */
  const selectContext = async (context: string): Promise<void> => {
    if (!user) {
      console.error("❌ No user found");
      return;
    }

    await selectContextWithUser(context, user);
  };

  /**
   * Refresh subscription entitlements for the given consumer data and scope.
   * - Non-blocking — call without await.
   * - Gated by feature flag `cameras.entitlement`; when disabled, no-op.
   * - `locationId` undefined → org-level scope (ALL devices across every location).
   * - ALWAYS calls `/api/v1/devices/entitlementConfigs` for the current scope's
   *   deviceIds, even when every deviceId is already present in the per-device
   *   cache. This function is invoked immediately after every
   *   `/api/v2/accounts?accountType={context}` call, so the entitlement values
   *   are refreshed from the server on every context/location change instead
   *   of being served from the local cache.
   */
  const refreshEntitlements = async (
    consumerData: any,
    locationId?: string,
  ): Promise<void> => {
    // Skip entirely when the feature flag is off
    if (!permissionService.isFeatureEnabled('cameras.entitlement')) {
      return;
    }

    const scopeDeviceIds = extractDeviceIdsFromConsumerData(consumerData, locationId);

    // Empty scope → clear any stored entitlement config (matches previous
    // "location deselected / no account" behaviour).
    if (scopeDeviceIds.length === 0) {
      permissionService.loadSubscriptionEntitlements({});
      return;
    }

    // Always fetch fresh per-device entitlement configs from the server — never
    // short-circuit based on cache contents. The cache is still updated with
    // the response so pilets reading `getPerDeviceEntitlementConfig()` see the
    // latest values.
    let perDeviceCache = readEntitlementDeviceCache();
    const { perDevice: fetched } = await apiService.getDeviceEntitlementConfigs(scopeDeviceIds);
    perDeviceCache = { ...perDeviceCache, ...fetched };
    writeEntitlementDeviceCache(perDeviceCache);

    // Merge in memory only — used to feed permissionService's Layer-2 flags.
    // Pilets consume the per-device cache directly via
    // `api.getPerDeviceEntitlementConfig()` and merge on their own side when
    // they need the scope-level view (see `mergeEntitlementConfigs` in the
    // camera pilet's utils/entitlement.ts). No separate `oneportal_entitlement_config`
    // localStorage entry is written — the per-device cache is the single source.
    const mergedConfig: EntitlementConfig | undefined = apiService.mergeCachedPerDeviceConfigs(
      perDeviceCache,
      scopeDeviceIds,
    );

    // Signal "loading in progress" so role-granted permissions remain visible
    // while the new scope's entitlement data is being merged.
    permissionService.loadSubscriptionEntitlements(null);

    if (mergedConfig) {
      // Combine clip- and snapshot-based AI search flags so VX/mixed fleets aren't misgated.
      const aiClip = mergedConfig.camera?.aiClipSearch;
      const aiSnapshot = mergedConfig.camera?.aiSnapshotSearch;
      const aiSearch =
        aiClip === undefined && aiSnapshot === undefined
          ? undefined
          : Boolean(aiClip) || Boolean(aiSnapshot);

      permissionService.loadSubscriptionEntitlements({
        ...(mergedConfig.camera?.snapshot !== undefined && { 'camera.snapshot': mergedConfig.camera.snapshot }),
        ...(mergedConfig.camera?.onDemandRecording !== undefined && { 'camera.on-demand-recording': mergedConfig.camera.onDemandRecording }),
        ...(aiSearch !== undefined && { 'camera.ai-search': aiSearch }),
      });
    }
  };

  /**
   * Check if role has changed for the selected location and refresh permissions if needed.
   * Called silently in the background on consumer-context location changes.
   */
  const refreshRoleForLocation = async (
    location: LocationData | null,
    currentContext: string,
    currentRole: string | null,
    currentUser: UserData | null,
  ): Promise<void> => {
    try {
      const consumerData = await apiService.getConsumerAccounts(currentContext, DeviceFilter.CAMERAS);
      const consumerUsers = consumerData?.data?.consumerUsers || [];

      let newRole: string | undefined;

      if (location) {
        // Find the consumerUser whose account contains this location
        const matchingUser = consumerUsers.find((cu: any) => {
          const account = cu?.consumerAccount;
          return (account?.locations || []).some(
            (loc: any) => loc?.id === location.id,
          );
        });
        newRole = matchingUser?.role || consumerUsers[0]?.role || undefined;
      } else {
        // My Organization – use first consumerUser's role
        newRole = consumerUsers[0]?.role || undefined;
      }

      console.log(
        `[Location Change] Location: "${location?.name || 'My Organization'}" | Role: ${newRole || currentRole || 'unknown'}`,
      );

      // Reuse the already-fetched response to sync LocationSelector without an extra API call.
      // Preserves the current company scope for multi-company COMMERCIAL accounts.
      try {
        localStorage.setItem('oneportal_consumer_data_full', JSON.stringify(consumerData));
        const rawIndex = localStorage.getItem('oneportal_selected_company_index');
        const companyIndex = rawIndex !== null ? Number(rawIndex) : NaN;
        const shouldScope =
          Number.isInteger(companyIndex) &&
          companyIndex >= 0 &&
          companyIndex < consumerUsers.length;
        const payload = shouldScope
          ? { ...consumerData, data: { ...(consumerData?.data ?? {}), consumerUsers: [consumerUsers[companyIndex]] } }
          : consumerData;
        localStorage.setItem('oneportal_consumer_data', JSON.stringify(payload));
      } catch {
        /* ignore storage errors */
      }
      window.dispatchEvent(new CustomEvent('oneportal:consumer-data-refreshed', { detail: consumerData }));

      if (!newRole || newRole === currentRole) {
        return; // Role unchanged – no action needed, no spinner needed
      }

      // Role changed – show spinner only now that we know a reload is necessary
      setIsRefreshingPermissions(true);

      // Refresh permissions
      const permissionsResponse =
        await apiService.getRolePermissions("CUSTOMER");
      const roleData = permissionsResponse.data?.roles?.find(
        (role: any) => role.roleName === newRole,
      );
      const rawPermissions = roleData?.permissions || [];

      setActiveRole(newRole);

      // Update stored user data with new role
      if (currentUser) {
        const contextData = currentUser.contexts.find(
          (c) => c.context === currentContext,
        );
        if (contextData) {
          contextData.roleName = newRole;
          contextData.permissions = rawPermissions as any;
          const updatedUser = { ...currentUser };
          setUser(updatedUser);
          localStorage.setItem(
            "oneportal_user",
            JSON.stringify(updatedUser),
          );
        }
      }

      await loadPermissionsForContext(currentContext, newRole, [
        { roleName: newRole, permissions: rawPermissions },
      ]);

    } catch (error) {
      console.error("Failed to check role for location:", error);
    } finally {
      setIsRefreshingPermissions(false);
    }
  };

  /**
   * Select a location within the current context.
   * For consumer contexts, silently checks if the role changed and refreshes permissions.
   */
  const selectLocation = (location: LocationData | null): void => {
    setSelectedLocation(location);
    if (location) {
      localStorage.setItem(
        "oneportal_selected_location",
        JSON.stringify(location),
      );
    } else {
      localStorage.removeItem("oneportal_selected_location");
    }
    // Notify pilets of location change so they can re-render
    window.dispatchEvent(
      new CustomEvent("oneportal:location-changed", { detail: location }),
    );

    console.log(
      `[Location Change] Switching to: "${location?.name || 'My Organization'}" | Current Role: ${activeRole || 'unknown'}`,
    );

    // Skip role check during initial context selection / session restore
    if (skipRoleCheckRef.current) {
      skipRoleCheckRef.current = false;
      return;
    }

    // For consumer contexts, check if the role changed and refresh permissions
    if (selectedContext && isConsumerContext(selectedContext)) {
      refreshRoleForLocation(
        location,
        selectedContext,
        activeRole,
        user,
      );

      // Refresh subscription entitlements for the newly selected scope.
      // - location != null → location-level scope (subset of deviceIds)
      // - location == null → org-level scope ("My Organization" — ALL deviceIds
      //   across every location of every consumer user).
      let consumerData: any = null;
      try {
        const raw = localStorage.getItem("oneportal_consumer_data");
        consumerData = raw ? JSON.parse(raw) : null;
      } catch {
        consumerData = null;
      }
      refreshEntitlements(consumerData, location?.id).catch(() => {
        /* non-blocking — ignore errors */
      });
    }
  };

  /**
    * Logout - clear all data and redirect to Auth0 logout
    */
  const logout = (): void => {
    console.log("🚪 Logout initiated");

    // Clear state
    setUser(null);
    setSelectedContext(null);
    setActiveRole(null);
    setLocations([]);
    setSelectedLocation(null);
    setConsumerUsers([]);
    setSelectedCompanyIndex(0);
    setIsInitializing(false);

    // Clear storage (camera ordering/view-size prefs are preserved)
    clearStoragePreservingPrefs();
    sessionStorage.clear();

    // Clear permission services
    permissionService.clear();
    permissionDataStore.clear();

    console.log("✅ Local state cleared");

    // Logout from Auth0 (clears Auth0 cookies)
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
        federated: true, // Force logout from identity provider
      },
    });
  };

  /**
   * Check if user has specific permission
   */
  const hasPermission = (permission: string): boolean => {
    if (!activeRole) return false;
    return permissionService.hasPermission(permission);
  };

  /**
   * Check if user has module permission
   */
  const hasModulePermission = (moduleId: ModuleId, action: ModuleAction): boolean => {
    if (!activeRole) return false;
    return permissionService.hasModulePermission(moduleId, action);
  };

  /**
   * Check if user has resource permission
   */
  const hasResourcePermission = (resource: ApiResource, action: ApiAction): boolean => {
    if (!activeRole) return false;
    return permissionService.hasResourcePermission(resource, action);
  };

  /**
   * Check if user can access module
   */
  const canAccessModule = (moduleId: ModuleId): boolean => {
    if (!activeRole) return false;
    return permissionService.canAccessModule(moduleId);
  };

  /**
   * Get all accessible modules
   */
  const getAccessibleModules = (): ModuleId[] => {
    if (!activeRole) return [];
    return permissionService.getAccessibleModules();
  };

  // Register unauthorized callback for automatic logout on token expiration
  useEffect(() => {
    setUnauthorizedCallback(() => {
      console.log("⚠️ Session expired - automatic logout triggered");
      sessionStorage.setItem('oneportal_return_to', window.location.pathname);
      logout();
    });

    return () => {
      setUnauthorizedCallback(() => { });
    };
  }, []);

  // Idle timeout - logout after 30 minutes of inactivity
  useIdleTimer({
    timeout: 30 * 60 * 1000, // 30 minutes
    warningTime: 30 * 1000, // 30 seconds warning
    onWarning: (remainingSeconds) => {
      if (user) {
        console.log(`⚠️ Idle warning - ${remainingSeconds} seconds until logout`);
        setIdleWarningSeconds(remainingSeconds);
        setShowIdleWarning(true);
      }
    },
    onIdle: () => {
      if (user) {
        console.log("⏱️ User idle timeout - automatic logout");
        setShowIdleWarning(false);
        sessionStorage.setItem('oneportal_idle_logout', 'true');
        logout();
      }
    },
  });

  const handleContinueSession = () => {
    console.log("✅ User continued session");
    setShowIdleWarning(false);
    // Activity is automatically tracked by useIdleTimer
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        selectedContext,
        activeRole,
        locations,
        selectedLocation,
        selectLocation,
        consumerUsers,
        selectedCompanyIndex,
        selectCompany,
        isInitializing,
        isSelectingContext,
        isLoadingPermissions,
        isRefreshingPermissions,
        login,
        selectContext,
        logout,
        showInvalidCredentialsModal,
        hasPermission,
        hasModulePermission,
        hasResourcePermission,
        canAccessModule,
        getAccessibleModules,
      }}
    >
      {children}
      <DynamicModalHost />
      <IdleWarningModal
        isOpen={showIdleWarning}
        remainingSeconds={idleWarningSeconds}
        onContinue={handleContinueSession}
      />
    </AuthContext.Provider>
  );
};
